import React from "react";
import HTML5Backend from "react-dnd-html5-backend";
import { DragDropContext } from "react-dnd";
import Rodal from "rodal";
import styled from "styled-components";
import { observable, action } from "mobx";
import { observer } from "mobx-react";
import _ from "lodash";

import Header from "./header";
import Explorer from "./explorer";
import Master from "./master";
import Detail from "./detail";
import Export from "../user/export";
import Catalog from "../catalog/catalog";
import { CustomDragLayer } from "../catalog/element";
import user, { changeScene } from "../user/index";
import BreadCrumb from "../canvas/breadcrumb.js";
import Canvas from "../canvas/index";
import SpreadTable from "../spreadsheet/table";
import { resizeBlocks } from "../canvas/store";
import { linkBlocks } from "../spreadsheet/store";
import Studio from "../studio/index";
import { KeyMapList } from "./hotkeys";
import Link from "../properties/linkui";
import * as Colors from "../reusable/colors";
import * as Icons from "../reusable/icons";

import "rodal/lib/rodal.css";

const fullDiv = {
  position: "absolute",
  padding: "inherit",
  top: 0,
  bottom: 0,
  right: 0,
  left: 0
};

export const Modals = {
  Spreadsheet: "spreadsheet",
  Export: "export",
  KeyMap: "keymap",
  Studio: "studio",
  LinkSettings: "linksettings",
  Scenes: "scenes"
};

const thumbWidth = 250;

const Thumbnails = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  background-color: ${Colors.secondary};
  padding: 20px 20px;
  min-height: 100%;
`;

const Thumbnail = styled.div`
  width: ${thumbWidth}px;
  border: 1px solid ${Colors.separatorDark};
  background: ${p =>
    p.preview
      ? 'linear-gradient(rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.3)), url("https://s3-eu-west-1.amazonaws.com/streamevolve/pattern-check.png")'
      : Colors.primary};
  border-color: ${p => p.active && Colors.highLightSecondary};
  overflow: hidden;
  height: ${p => p.height}px;
  margin: 15px;
  line-height: ${p => p.height}px;
  h3 {
    font-weight: ${p => p.active && "bold"};
    color: ${p => (p.active ? Colors.TextHeader : Colors.Text)};
  }
  cursor: pointer;
  text-align: center;
`;

// const Scene = styled.div`
//   thumbnail {
//     border-width: ${p => p.active && "5px"};
//   }
// `;

const Scenes = observer(props => (
  <Thumbnails>
    {_.map(user.scenes, (s, id) => (
      <Thumbnail
        key={id}
        active={id === user.currentScene}
        onClick={() => {
          changeScene(id);
          closeModal();
          state.scenePreview = false;
        }}
        height={thumbWidth / (s.canvas.streamWidth / s.canvas.streamHeight)}
        preview={state.scenePreview === id}
        onMouseEnter={() => (state.scenePreview = id)}
        onMouseLeave={() => (state.scenePreview = false)}>
        {state.scenePreview === id ? (
          <Canvas blocks={resizeBlocks(linkBlocks(s), 200, false)} editing={false} />
        ) : (
          <h3>{s.canvas.label}</h3>
        )}
      </Thumbnail>
    ))}
  </Thumbnails>
));

const ModalStyles = {
  [Modals.Spreadsheet]: {
    width: 95,
    height: 80,
    measure: "%",
    customStyles: { overflowY: "auto", backgroundColor: Colors.secondary },
    showCloseButton: false
  },
  [Modals.Export]: {
    width: 500,
    height: 500,
    measure: "px",
    customStyles: { overflowY: "auto", backgroundColor: Colors.secondary },
    showCloseButton: false
  },
  [Modals.KeyMap]: {
    width: 60,
    height: 80,
    measure: "%",
    customStyles: { overflowY: "auto" },
    showCloseButton: false
  },
  [Modals.Studio]: {
    width: 700,
    height: 800,
    measure: "px",
    customStyles: { overflowY: "auto", backgroundColor: Colors.secondary },
    showCloseButton: false
  },
  [Modals.LinkSettings]: {
    width: 500,
    height: 600,
    measure: "px",
    customStyles: { overflowY: "auto" },
    showCloseButton: false
  },
  [Modals.Scenes]: {
    width: 70,
    height: 70,
    measure: "%",
    customStyles: { overflowY: "auto", padding: "0px" },
    showCloseButton: false
  }
};

const modalComponent = modal => {
  return {
    [Modals.Spreadsheet]: () => <SpreadTable />,
    [Modals.Export]: () => <Export />,
    [Modals.KeyMap]: () => <KeyMapList />,
    [Modals.Studio]: () => <Studio />,
    [Modals.LinkSettings]: () => <Link {...state.modalProps} />,
    [Modals.Scenes]: () => <Scenes />
  }[modal];
};

const Page = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  overflow: hidden;
  color: ${Colors.Text};
`;

const Main = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const CanvasWrapper = styled.div`
  flex-grow: 1;
  background-color: #ccc;
  position: relative;
  padding: 9px 10px;
`;

const Controls = styled.div`
  &:hover i {
    color: ${Colors.hoverSecondary};
  }
  & a {
    color: ${Colors.Text};
  }
  cursor: pointer;
  font-size: 15px;
  line-height: 15px;
  margin-bottom: 10px;
  float: right;
`;

const state = observable({
  modal: false,
  modalProps: undefined,
  catalog: false,
  scenePreview: false
});

export const showCatalog = action("showCatalog", () => {
  state.catalog = true;
});

export const toggleCatalog = action("toggleCatalog", () => {
  state.catalog = !state.catalog;
});

export const hideCatalog = action("hide Catalog", () => {
  state.catalog = false;
});

export const setModal = action("setModal", (m, p) => {
  state.modal = m;
  state.modalProps = p;
});
export const closeModal = () => setModal(false);

const standalones = {
  [Modals.Studio]: "/studio",
  [Modals.Spreadsheet]: "/database"
};

const Editor = observer(p => {
  return (
    <Page>
      <Explorer />
      <Main>
        <Header showModal={m => setModal(m)} />
        <CanvasWrapper>
          <div style={fullDiv}>
            <Master />
          </div>
        </CanvasWrapper>
        <BreadCrumb />
      </Main>
      <Detail />
      <Rodal
        visible={state.modal !== false}
        onClose={() => closeModal()}
        {...ModalStyles[state.modal]}>
        {standalones[state.modal] && (
          <Controls top="8px" right="8px">
            <a href={standalones[state.modal]} target="_blank">
              <Icons.ExternalLink />
            </a>
          </Controls>
        )}
        {modalComponent(state.modal) && modalComponent(state.modal)()}
      </Rodal>
      <Catalog hide={!state.catalog} />
      <CustomDragLayer />
    </Page>
  );
});

export default DragDropContext(HTML5Backend)(Editor);



// WEBPACK FOOTER //
// ./src/layout/editor.js